<template>
  <ai-card :radius="16" class="product-workarea" :class="{ 'product-workarea--mobile': isMobile }">
    <div v-if="!loading" class="product-workarea__actions d-flex align-center mb-2">
      <div class="product-workarea__zoom">
        <ai-clear-button class="product-workarea__zoom__button" :active-zoom="activeZoom" @click="zoomOutHandler">
          <inline-svg :src="require('@/assets/icons/IconDecrease.svg')" />
        </ai-clear-button>
        <ai-clear-button class="product-workarea__zoom__button" @click="zoomInHandler">
          <inline-svg :src="require('@/assets/icons/IconIncrease.svg')" />
        </ai-clear-button>
      </div>
      <v-spacer />
      <ai-clear-button @click="addProductModalToggleHandler" class="d-flex align-center product-workarea__action">
        <inline-svg :src="require('@/assets/icons/menu/my-products.svg')" />
        {{ $t('editor.settings.add-product') }}
      </ai-clear-button>
      <ai-clear-button
        v-if="activeProductId && productsList.length > 1"
        @click="removeProductHandler(activeProductId)"
        class="d-flex align-center product-workarea__action"
      >
        <inline-svg :src="require('@/assets/icons/clear.svg')" />
        {{ $t('common.delete') }}
      </ai-clear-button>
    </div>
    <div class="product-workarea__image">
      <div v-if="activeProductId && productsList.length > 1" class="product-workarea__layers">
        <ai-clear-button class="product-workarea__layers__button" @click="toTopHandler">
          <inline-svg :src="require('@/assets/icons/canvas/to-top.svg')" />
        </ai-clear-button>
        <ai-clear-button class="product-workarea__layers__button" @click="toBottomHandler">
          <inline-svg :src="require('@/assets/icons/canvas/to-bottom.svg')" />
        </ai-clear-button>
      </div>
      <ai-canvas
        v-if="!loading"
        :key="key"
        :images="productsList"
        :disabled="generateImageData.loading"
        :width="generateImageData.canvas.width"
        :height="generateImageData.canvas.height"
        :active-zoom="activeZoom"
        @canvas-update="canvasUpdateHandler"
        @delete-product="removeProductHandler"
        @active-zoom-set="activeZoomSetHandler"
        :reset-existing-image-transforms-on-change-images="false"
        add-lines
        ref="aiCanvasRef"
      />
      <ai-sceleton v-else class="product-workarea__sceleton" />
      <ai-drop-loader v-if="generateImageData.loading" />
      <!-- <v-progress-circular v-else color="white" indeterminate :size="77" :width="12" /> -->
    </div>
  </ai-card>
  <ai-modal :show-modal="showAddingModal" :handle-toggle="addProductModalToggleHandler">
    <template v-slot:button>
      <add-image is-remove-bg />
    </template>
  </ai-modal>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { v4 as uuidv4 } from 'uuid';
import { cloneDeep } from 'lodash';
import { useEventBus } from '@vueuse/core';

// import { urlToBase64 } from '@/helpers/urlToBase64';

import { useEditorStore } from '@/store/editor';
import { useCommonStore } from '@/store/common';
import { useServicesStore } from '@/store/services';

import { ICanvasImage } from '@/types/ICanvasImage';
// import { IImageData, IProduct } from '@/store/editor/types';
// import { IServiceImagesItem } from '@/types/IServiceImagesItem';

import AiCard from '@/components/UI/AiCard/AiCard.vue';
import AiClearButton from '@/components/UI/AiClearButton/AiClearButton.vue';
import AiCanvas from '@/components/UI/AiCanvas/AiCanvas.vue';
import AiDropLoader from '@/components/UI/AiDropLoader/AiDropLoader.vue';
import AiModal from '@/components/UI/AiModal/AiModal.vue';
import AiSceleton from '@/components/UI/AiSceleton/AiSceleton.vue';

import AddImage from '@/components/common/AddImage/AddImage.vue';

const busError = useEventBus<string>('toast-error');

// const emits = defineEmits(['keyUpdate']);

const { generateImageData, aiCanvasRef } = storeToRefs(useEditorStore());
const { imageForActions } = storeToRefs(useServicesStore());
const { isMobile } = storeToRefs(useCommonStore());

const loading = ref<boolean>(false);
const activeProductId = ref<string | undefined>(undefined);
const activeProductUid = ref<string | undefined>(undefined);
const productsList = ref<ICanvasImage[]>([]);
const activeZoom = ref<number>(1);
const key = ref<number>(0);
const showAddingModal = ref<boolean>(false);

const canvasUpdateHandler = (productsData: {
  products: any[];
  activeProductId: any | null;
  activeProductUid: string;
}): void => {
  activeProductId.value = productsData.activeProductId;
  activeProductUid.value = productsData.activeProductUid;
  const productsUpdated: any = [];

  productsData.products.forEach((product: any) => {
    const resProduct = cloneDeep(
      productsList.value.find((productsListItem: any) => productsListItem.id === product.productId),
    );
    if (resProduct) {
      const productForUpdate = { ...product, image: resProduct.image, id: product.productId };
      productsUpdated.push(productForUpdate);
    }
  });
  productsList.value = [...productsUpdated];
  generateImageData.value.products = productsList.value as any;

  // productsList.value = [...productsData.products];
};

const removeProductHandler = (id: string | undefined): void => {
  if (productsList.value.length <= 1) {
    busError.emit('Нельзя удалить');
    return;
  }
  productsList.value = productsList.value.filter((p) => p.id !== id);
  key.value += 1;
};

const zoomInHandler = (): void => {
  activeZoom.value += 0.1;
};

const zoomOutHandler = (): void => {
  activeZoom.value -= 0.1;
};

const activeZoomSetHandler = (newVal: number): void => {
  activeZoom.value = newVal;
};

const toTopHandler = (): void => {
  const uid = activeProductUid.value;
  aiCanvasRef.value.moveForvard(uid);
};

const toBottomHandler = (): void => {
  const uid = activeProductUid.value;
  aiCanvasRef.value.moveBackward(uid);
};

const addProductModalToggleHandler = (): void => {
  showAddingModal.value = !showAddingModal.value;
};

onMounted(() => {
  // productsList.value = generateImageData.value.products as any;
  if (generateImageData.value.products.length) {
    const products = [...generateImageData.value.products];
    productsList.value = products;
  } else {
    generateImageData.value.products = [
      {
        image: imageForActions.value,
        id: uuidv4(),
        uid: uuidv4(),
        mask: false,
        transform: {
          x: null,
          y: 0,
          scaleX: 1,
          scaleY: 1,
          angle: 0,
        },
      },
    ];
  }
});

watch(
  () => generateImageData.value.products,
  () => {
    const products = [...generateImageData.value.products];
    productsList.value = products;
    // products.forEach((product: IImageData, index: number) => {
    //   if (productsList.value[index]) {
    //     productsList.value[index].transform = { ...product.transform };
    //   }
    // });
  },
  { deep: true },
);

watch(imageForActions, (newVal) => {
  if (newVal !== '') {
    showAddingModal.value = false;
    generateImageData.value.products.push({
      image: newVal,
      id: uuidv4(),
      uid: uuidv4(),
      mask: false,
      transform: {
        x: null,
        y: 0,
        scaleX: 1,
        scaleY: 1,
        angle: 0,
      },
    });
    const products = [...generateImageData.value.products];
    productsList.value = products;
    key.value += 1;
  } else {
    generateImageData.value.products = [];
  }
});
</script>

<style scoped lang="scss" src="./ProductWorkarea.scss" />
